'use client'

import { useState } from 'react'

const oneStep = [
  { size: '$10,000',  fee: '$160',   badge: null,          special: null,     url: 'https://trade.crypto.st/en/challenges?planid=88' },
  { size: '$25,000',  fee: '$330',   badge: 'Most traded', special: null,     url: 'https://trade.crypto.st/en/challenges?planid=89' },
  { size: '$50,000',  fee: '$550',   badge: null,          special: null,     url: 'https://trade.crypto.st/en/challenges?planid=90' },
  { size: '$100,000', fee: '$1,050', badge: null,          special: null,     url: 'https://trade.crypto.st/en/challenges?planid=91' },
  { size: '$200,000', fee: '$2,150', badge: null,          special: null,     url: 'https://trade.crypto.st/en/challenges?planid=92' },
  { size: '$500,000', fee: null,     badge: null,          special: 'invite', url: '#contact' },
]

const twoStep = [
  { size: '$10,000',  fee: '$98',  badge: null,          special: null,     url: 'https://trade.crypto.st/en/challenges?planid=106' },
  { size: '$25,000',  fee: '$280', badge: 'Most traded', special: null,     url: 'https://trade.crypto.st/en/challenges?planid=107' },
  { size: '$50,000',  fee: '$450', badge: null,          special: null,     url: 'https://trade.crypto.st/en/challenges?planid=108' },
  { size: '$100,000', fee: '$950', badge: null,          special: null,     url: 'https://trade.crypto.st/en/challenges?planid=109' },
  { size: '$200,000', fee: '$2000', badge: null,          special: null,     url: 'https://trade.crypto.st/en/challenges?planid=110' },
  { size: '$500,000', fee: null,   badge: null,          special: 'invite', url: '#contact' },
]

type Plan = typeof oneStep[0]


const includes: Record<string, string[]> = {
  '1': [
    'Up to 90% profit split',
    'On-demand crypto payouts',
  ],
  '2': [
    'Up to 80% profit split',
    'On-demand crypto payouts',
  ],
}

export default function ChooseAccountSize() {
  const [active, setActive]     = useState<'1' | '2'>('1')
  const [selected, setSelected] = useState<Plan>(oneStep[1])
  const plans    = active === '1' ? oneStep : twoStep
  const isInvite = selected.special === 'invite'

  function handleTabChange(tab: '1' | '2') {
    setActive(tab)
    const currentIndex = (active === '1' ? oneStep : twoStep).findIndex(p => p.size === selected.size)
    setSelected(tab === '1' ? oneStep[currentIndex] : twoStep[currentIndex])
  }

  return (
    <section id="sizes" className="bg-black text-white py-20 px-4 border-t border-zinc-800">
      <div className="max-w-5xl mx-auto">

        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-sm uppercase tracking-[0.1em] text-zinc-300 mb-4">Funded crypto trading accounts</h2>
          <h3 className="text-3xl lg:text-5xl font-bold font-[family-name:var(--font-saira)] leading-tight">
            Choose your account size
          </h3>
        </div>

        <div className="flex flex-col lg:flex-row gap-px bg-zinc-800">

          {/* LEFT */}
          <div className="flex-1 bg-black p-6 lg:p-8 flex flex-col gap-6">

            {/* Challenge type */}
            <div>
              <p className="text-xs uppercase tracking-[0.1em] text-zinc-400 mb-3">Challenge type</p>
              <div className="flex border border-zinc-800">
                {(['1', '2'] as const).map((tab) => (
                  <button
                    key={tab}
                    onClick={() => handleTabChange(tab)}
                    className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-bold uppercase tracking-widest transition-all duration-300 font-[family-name:var(--font-saira)]
                      ${active === tab ? 'bg-[#5841D8] text-white' : 'bg-black text-zinc-400 hover:text-zinc-300'}`}
                  >
                    <span className={`w-5 h-5 flex items-center justify-center text-xs border ${active === tab ? 'border-white/30' : 'border-zinc-700'}`}>{tab}</span>
                    {tab === '1' ? 'One-step' : 'Two-step'}
                  </button>
                ))}
              </div>
            </div>

            {/* Account size table */}
            <div>
              {/* Table header */}
              <div className="flex items-center justify-between px-5 py-2 border border-zinc-800 bg-zinc-950 mb-px">
                <p className="text-xs uppercase tracking-[0.1em] text-zinc-400">Trading capital</p>
                <p className="text-xs uppercase tracking-[0.1em] text-zinc-400">Fee</p>
              </div>

              {/* Rows */}
              <div className="flex flex-col gap-px bg-zinc-800">
                {plans.map((plan) => {
                  const isSelected   = selected.size === plan.size
                  const isFeatured   = plan.badge === 'Most traded'
                  const isPlanInvite = plan.special === 'invite'

                  return (
                    <button
                      key={plan.size}
                      onClick={() => setSelected(plan)}
                      className={`flex items-center gap-4 px-5 py-4 text-left transition-all duration-200 w-full
                        ${isSelected ? 'bg-zinc-900 border-l-2 border-l-[#25B362]' : 'bg-black hover:bg-zinc-950 border-l-2 border-l-transparent'}`}
                    >
                      {/* Radio */}
                      <div className={`w-4 h-4 flex-shrink-0 border flex items-center justify-center transition-colors
                        ${isSelected ? 'border-[#25B362]' : 'border-zinc-600'}`}>
                        {isSelected && <div className="w-2 h-2 bg-[#25B362]" />}
                      </div>

                      {/* Size + badge */}
                      <div className="flex items-center gap-2 flex-1">
                        <span className={`font-bold text-base font-[family-name:var(--font-saira)] ${isPlanInvite ? 'text-[#8B7CF6]' : isSelected ? 'text-white' : 'text-zinc-300'}`}>
                          {plan.size}
                        </span>
                        {isFeatured && (
                          <span className="text-[10px] uppercase tracking-widest text-black bg-[#25B362] px-2 py-0.5 font-bold hidden sm:inline">
                            Popular
                          </span>
                        )}
                        {isPlanInvite && (
                          <span className="text-[10px] uppercase tracking-widest text-[#8B7CF6] border border-[#8B7CF6] px-2 py-0.5 hidden sm:inline">
                            Invite only
                          </span>
                        )}
                      </div>

                      {/* Fee */}
                      <span className={`text-sm font-semibold font-[family-name:var(--font-saira)] ${isSelected ? 'text-[#25B362]' : 'text-zinc-400'}`}>
                        {plan.fee ?? '—'}
                      </span>
                    </button>
                  )
                })}
              </div>
            </div>

          </div>

          {/* RIGHT — summary */}
          <div className="lg:w-72 bg-zinc-950 p-6 lg:p-8 flex flex-col gap-6 border-t lg:border-t-0 border-zinc-800">

          <div>
  <p className="text-xs uppercase tracking-[0.1em] text-zinc-400 mb-3">Your selection</p>
  <div className="flex items-center gap-2">
    <span className="text-xs font-bold uppercase tracking-widest px-2 py-1 bg-[#5841D8] text-white font-[family-name:var(--font-saira)]">
      {active === '1' ? 'One-step' : 'Two-step'}
    </span>
    <span className="text-xs font-bold uppercase tracking-widest px-2 py-1 bg-[#25B362] text-black font-[family-name:var(--font-saira)]">
      {selected.size}
    </span>
  </div>
</div>

            {/* Price */}
            <div className="border-t border-zinc-800 pt-6">
              {isInvite ? (
                <p className="text-2xl font-bold text-[#8B7CF6] font-[family-name:var(--font-saira)]">Invite only</p>
              ) : (
                <>
                  <p className="text-xs uppercase tracking-[0.1em] text-zinc-400 mb-1">Evaluation fee</p>
                  <p className="text-4xl font-extrabold text-white font-[family-name:var(--font-saira)]">{selected.fee}</p>
                </>
              )}
            </div>

            {/* Includes */}
            <div className="border-t border-zinc-800 pt-6 flex-1">
              <p className="text-xs uppercase tracking-[0.1em] text-zinc-400 mb-4">Your challenge includes</p>
              <ul className="flex flex-col gap-3">
                {[`${selected.size} trading capital`, ...includes[active]].map((item, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <svg width="14" height="14" viewBox="0 0 18 18" fill="none" className="flex-shrink-0 mt-0.5">
                      <circle cx="9" cy="9" r="8.25" stroke="#25B362" strokeWidth="1.2"/>
                      <path d="M5.5 9l2.5 2.5 4.5-4.5" stroke="#25B362" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span className="text-xs text-zinc-300 leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* CTA */}
            
            <a
  href={selected.url}
  target="_blank"
  rel="noopener noreferrer"
  className={`flex items-center justify-center gap-2 w-full border-2 text-sm font-bold uppercase tracking-widest py-4 transition-all duration-300 font-[family-name:var(--font-saira)]
    ${isInvite
      ? 'border-[#8B7CF6] text-[#8B7CF6] hover:bg-[#5841D8] hover:border-[#5841D8] hover:text-white hover:font-bold'
      : 'border-green-500 text-green-500 hover:bg-green-500 hover:border-green-500 hover:text-black hover:font-bold'
    }`}
>
  {isInvite ? 'Request invite' : 'Start challenge'}
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M5 12h14M12 5l7 7-7 7"/>
  </svg>
</a>

          </div>
        </div>

      </div>


    </section>
  )
}